/**
 * Portal settings (`GET api/procedures/public/settings`, anonymous). Every field has a typed default
 * so screens render before, or without, the API; the last answer is kept on the phone for offline use.
 */
import { useQuery } from '@tanstack/react-query';
import { usePublicProceduresService, type ProcedurePublicSettingsView } from '@actschurch/ccms-sdk';
import { proceduresKeys } from './queryKeys';
import { getSettingsSnapshot, saveSettingsSnapshot } from './offlineStore';

export const DEFAULT_SETTINGS: ProcedurePublicSettingsView = {
  portalName: 'Acts Procedures',
  publicRunsEnabled: true,
  publicAiChatEnabled: false,
  planningCentreLoginEnabled: true,
  requireRecaptchaForPublicRuns: false,
  recaptchaSiteKey: null,
  welcomeText: null,
  supportText: null,
  aiChatServiceEnabled: false,
};

export function usePortalSettings() {
  const publicProcedures = usePublicProceduresService();
  const query = useQuery({
    queryKey: proceduresKeys.settings(),
    queryFn: async () => {
      const s = await publicProcedures.getPublicSettings();
      saveSettingsSnapshot(s);
      return s;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    placeholderData: () => getSettingsSnapshot() ?? undefined,
  });
  const settings: ProcedurePublicSettingsView = { ...DEFAULT_SETTINGS, ...(query.data ?? {}) };
  if (!settings.portalName) settings.portalName = DEFAULT_SETTINGS.portalName;
  return { settings, isLoading: query.isLoading, isFetched: query.isFetched };
}
